import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HiOutlineMagnifyingGlass,
  HiOutlineBuildingOffice2,
  HiOutlineMapPin,
  HiChevronRight,
  HiOutlineFunnel,
  HiPlus,
  HiPencil,
  HiTrash,
  HiOutlineCurrencyDollar,
  HiOutlineDocumentText
} from 'react-icons/hi2';
import { getAllLocalPlans, deleteLocalPlan } from 'crud/configuration_api/local_plans';
import { getAllLocalPlanPayments } from 'crud/configuration_api/local_plan_payments'; 
import MiniLoadingSpinner from '../../../components/Spinners/MiniLoadingSpinner';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import './LocalPlans.scss';
import { Icon, X } from 'react-bootstrap-icons';
import '../Home/Home.scss';
import '../../../styles/Table.scss';
import { Divider } from 'views/Restaurant/Caja/Ventas/components/GeneralSales/components/SessionBriefModal/components';
import CreateLocalPlan from './components/CreateLocalPlan/CreateLocalPlan';
import UpdateLocalPlan from './components/UpdateLocalPlan/UpdateLocalPlan';
import CreateLocalPlanPayment from './components/CreateLocalPlanPayment/CreateLocalPlanPayment';
import UpdateLocalPlanPayment from './components/UpdateLocalPlanPayment/UpdateLocalPlanPayment';

interface LocalPlan {
  local_plan_id: number;
  local_id: number;
  local_name: string;
    agreed_price: number;
    month_day_billing: number;
    visits_number?: number;
    contract_start_date: string;
    trial_start_date?: string;
    trial_duration_days?: number;
    is_active: boolean;
    canceled_at?: string;
    cancellation_reason?: string;
    local_plans_metadata?: any;
}


interface LocalPlanPayment{
    local_plan_payment_id: number;
    local_plan_id: number;
    local_id: number;
    payment_amount: number;
    payment_date: string;
    days_since_last_payment?: number;
    created_by_user_id?: number;
    local_plans_payments_metadata?: any;
}

interface dashboardStats{
    mensualEarnings: number;
    mensualProjectedEarnings: number;
    testingLocals: number;
    definitiveLocals: number;
}

const LocalPlans: React.FC = () => {
  const navigate = useNavigate();
  const [localPlans, setLocalPlans] = useState<LocalPlan[]>([]);
  const [localPlansPayments, setLocalPlansPayments] = useState<LocalPlanPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mensualEarnings, setMensualEarnings] = useState<number>(0);
  const [mensualProjectedEarnings, setMensualProjectedEarnings] = useState<number>(0);
  const [testingLocals, setTestingLocals] = useState<number>(0);
  const [definitiveLocals, setDefinitiveLocals] = useState<number>(0);
  const [stats, setStats] = useState<dashboardStats>({} as dashboardStats);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<LocalPlan | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<LocalPlan | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showCreatePaymentModal, setShowCreatePaymentModal] = useState(false);
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState<LocalPlan | null>(null);

  useEffect(() => {
    fetchLocalPlans();
  }, []);

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  const handleOpenUpdateModal = (plan: LocalPlan) => {
    setSelectedPlan(plan);
    setShowUpdateModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowUpdateModal(false);
    setSelectedPlan(null);
  };

  const handlePlanCreated = () => {
    fetchLocalPlans();
    handleCloseModals();
  };

  const handlePlanUpdated = () => {
    fetchLocalPlans();
    handleCloseModals();
  };

  const handleOpenDeleteModal = (plan: LocalPlan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setPlanToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete) return;

    setDeleting(true);
    try {
      const response = await deleteLocalPlan(planToDelete.local_plan_id);
      if ((response as any).status === 200) {
        toast.success('Plan eliminado exitosamente');
        fetchLocalPlans();
        handleCloseDeleteModal();
      } else {
        toast.error('Error al eliminar el plan');
      }
    } catch (err: any) {
      console.error('Error deleting plan:', err);
      const errorMsg = err?.response?.data?.message || 'Error al eliminar el plan';
      toast.error(errorMsg);
    }
    setDeleting(false);
  };

  const handleOpenCreatePaymentModal = (plan?: LocalPlan) => {
    if (plan) {
      setSelectedPlanForPayment(plan);
    }
    setShowCreatePaymentModal(true);
  };

  const handleOpenPaymentDetailsModal = (plan: LocalPlan) => {
    setSelectedPlanForPayment(plan);
    setShowPaymentDetailsModal(true);
  };

  const handleClosePaymentModals = () => {
    setShowCreatePaymentModal(false);
    setShowPaymentDetailsModal(false);
    setSelectedPlanForPayment(null);
  };

  const handlePaymentCreated = () => {
    fetchLocalPlans();
    handleClosePaymentModals();
  };

  const fetchLocalPlans = async () => {
    setLoading(true);
    setError(null);
    try {
        const response = await getAllLocalPlans();
        if (response.status === 200) {
            setLocalPlans(response.data);
            setStats({
                mensualEarnings: response.data.reduce((sum: number, plan: LocalPlan) => sum + (plan.is_active ? plan.agreed_price : 0), 0),
                mensualProjectedEarnings: response.data.reduce((sum: number, plan: LocalPlan) => sum + plan.agreed_price, 0),
                testingLocals: response.data.filter((plan: LocalPlan) => plan.trial_start_date).length,
                definitiveLocals: response.data.filter((plan: LocalPlan) => !plan.trial_start_date).length,
            });
            setError(null);
        } else {
            setError('Error fetching local plans.');
        }
    } catch (err) {
        console.error('Error fetching local plans:', err);
        setError('Error fetching local plans.');
    }
    setLoading(false);
  };



  const filteredLocalPlans = useMemo(() => {
    const result = localPlans.filter(plan => {
      const localName = plan.local_name;
      return localName && 
        localName.toLowerCase().includes(searchTerm.toLowerCase());
    });
    console.log('Filtered Local Plans:', result);
    return result;
  }, [localPlans, searchTerm]);

  const RenderTable: React.FC<{
    plans: LocalPlan[];
    title?: string;
  }> = ({ plans, title }) => {
    if (plans.length === 0) {
      return (
        <div className="no-data-available">
          <p className="no-data-available-message">No hay planes {title?.toLowerCase() || 'disponibles'}</p>
        </div>
      );
    }

    const isInTrialPeriod = (plan: LocalPlan): boolean => {
      if (!plan.trial_start_date || !plan.trial_duration_days) {
        return false;
      }
      
      const trialStartDate = new Date(plan.trial_start_date);
      const today = new Date();
      const trialEndDate = new Date(trialStartDate);
      trialEndDate.setDate(trialStartDate.getDate() + plan.trial_duration_days);
      
      return today <= trialEndDate;
    };

    return (
      <div className="scroll-container-table">
        <table className="custom-table">
          <thead>
            <tr>
              <th className="no_pagination">Nombre del Local</th>
              <th className="no_pagination">Valor</th>
              <th className="no_pagination">Estado</th>
              <th className="no_pagination">Fecha próximo pago</th>
              <th className="no_pagination no-responsive">Número de pagos</th>
              <th className="no_pagination no-responsive">Número de visitas</th>
              <th className="no_pagination no-responsive">Periodo de prueba</th>
              <th className="no_pagination" style={{ width: '80px', textAlign: 'center' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {plans.map(plan => (
              <tr key={plan.local_plan_id}>
                <td>{plan.local_name}</td>
                <td>{plan.agreed_price.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 })}</td>
                <td>
                  {plan.is_active ? (
                    <span className="table-cell-status success">Activo</span>
                  ) : (
                    <span className="table-cell-status unactive">Inactivo</span>
                  )}
                </td>
                <td>{plan.local_plans_metadata?.next_payment_date || 'N/A'}</td>
                <td className="no-responsive">{plan.local_plans_metadata?.payments_count || 0}</td>
                <td className="no-responsive">{plan.visits_number || 0}</td>
                <td className="no-responsive" style={{ textAlign: 'center', fontSize: '1.5rem' }}>
                  {plan.trial_start_date ? (
                    isInTrialPeriod(plan) ? '✓' : '✗'
                  ) : (
                    '✗'
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleOpenUpdateModal(plan)}
                      className="action-button edit-button"
                      title="Editar plan"
                    >
                      <HiPencil size={18} />
                    </button>
                    <button
                      onClick={() => handleOpenCreatePaymentModal(plan)}
                      className="action-button payment-button"
                      title="Registrar pago"
                    >
                      <HiOutlineCurrencyDollar size={18} />
                    </button>
                    <button
                      onClick={() => handleOpenPaymentDetailsModal(plan)}
                      className="action-button payment-button"
                      title="Ver pagos"
                    >
                      <HiOutlineDocumentText size={18} />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteModal(plan)}
                      className="action-button delete-button"
                      title="Eliminar plan"
                    >
                      <HiTrash size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const StatCard: React.FC<({
    title: string;
    value: string | number;
    icon: React.ElementType;
    color: string;
  })> = ({ title, value, icon: Icon, color }) => {
    return (
      <div className="stat-card">
        <div className="stat-card-header">
          <div className={`stat-icon-wrapper ${color}`}>
            <Icon className="stat-icon" size={24} />
          </div>
        </div>
        <div className="stat-card-body">
          <h3 className="stat-value">{value}</h3>
          <p className="stat-title">{title}</p>
        </div>
      </div>
    );
  };



  return (
    <div className="admin-home">
      <div className="admin-home-header">
        <h1>Planes de Locales</h1>
        <p>Gestión de planes y facturación de locales</p>
      </div>
      
      <div className='stats-grid'>
        <StatCard
          title="Ganancias Mensuales"
          value={stats.mensualEarnings ? stats.mensualEarnings.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }) : 0}
          icon={HiOutlineBuildingOffice2}
          color="green"
        />
        <StatCard
          title="Ganancias Mensuales Proyectadas"
          value={stats.mensualProjectedEarnings ? stats.mensualProjectedEarnings.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }) : 0}
          icon={HiOutlineMapPin}
          color="blue"
        />
        <StatCard
          title="Locales en Prueba"
          value={stats.testingLocals ?? 0}
          icon={HiOutlineFunnel}
          color="orange"
        />
        <StatCard
          title="Locales Definitivos"
          value={stats.definitiveLocals ?? 0}
          icon={HiOutlineBuildingOffice2}
          color="purple"
        />
      </div>

      <div className="local-plans-section">
        <div className="section-header">
          <h2>Planes de Locales</h2>
          <div className="header-buttons">
            <button className="create-plan-button" onClick={handleOpenCreateModal}>
              <HiPlus size={20} />
              <span>Crear Plan</span>
            </button>
          </div>
        </div>
        <RenderTable plans={filteredLocalPlans} title="disponibles" />
      </div>

      <CreateLocalPlan
        show={showCreateModal}
        onHide={handleCloseModals}
        onPlanCreated={handlePlanCreated}
      />

      <UpdateLocalPlan
        show={showUpdateModal}
        onHide={handleCloseModals}
        onPlanUpdated={handlePlanUpdated}
        plan={selectedPlan}
      />

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        className="delete-confirmation-modal"
      >
        <Modal.Header closeButton={false}>
          <div className="form-modal-title-container">
            <h2 className="form-modal-title">Confirmar Eliminación</h2>
          </div>
          <button 
            className="modal-close-button"
            onClick={handleCloseDeleteModal}
            disabled={deleting}
          >
            <X size={20} />
          </button>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: 'var(--text-color)', marginBottom: '1rem' }}>
            ¿Estás seguro de que deseas eliminar el plan de <strong>{planToDelete?.local_name}</strong>?
          </p>
          <p style={{ color: 'var(--text-color-muted)', fontSize: '0.875rem', marginBottom: 0 }}>
            Esta acción no se puede deshacer.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <button
              type="button"
              className="btn form-button-cancel"
              onClick={handleCloseDeleteModal}
              disabled={deleting}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn form-button-delete"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <MiniLoadingSpinner />
                  <span style={{ marginLeft: '0.5rem' }}>Eliminando...</span>
                </>
              ) : (
                'Eliminar'
              )}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Payment Modals */}
      <CreateLocalPlanPayment 
        show={showCreatePaymentModal}
        onHide={handleClosePaymentModals}
        onPaymentCreated={handlePaymentCreated}
        localPlans={localPlans}
        localId={selectedPlanForPayment?.local_id}
        localName={selectedPlanForPayment?.local_name}
      />

      <UpdateLocalPlanPayment 
        show={showPaymentDetailsModal}
        onHide={handleClosePaymentModals}
        localPlanId={selectedPlanForPayment?.local_plan_id || null}
        localName={selectedPlanForPayment?.local_name || ''}
        onPaymentDeleted={handlePaymentCreated}
      />
    </div>
  );
};

export default LocalPlans;
